/* eslint-disable no-unused-vars */
import React, { createContext, useContext, ReactNode, useEffect, useCallback } from 'react';
import { BrandConfig } from '../../config/brandConfig';
import { useBrandTheme, BrandTheme } from '../../Theme/useBrandTheme';

interface BrandContextType {
  brandConfig: BrandConfig;
  brandTheme: BrandTheme;
  isFeatureEnabled: (feature: keyof BrandConfig['features']) => boolean;
  updatePageMeta: () => void;
}

/**
 * Hex de marque vers triplet « r, g, b », pour composer des rgba() en CSS
 * (halos de focus des boutons) à partir d'une seule couleur de marque.
 */
const hexToRgbTriplet = (hex: string): string => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const value = parseInt(full, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255].join(', ');
};

const BrandContext = createContext<BrandContextType | undefined>(undefined);

interface BrandProviderProps {
  children: ReactNode;
  forceBrand?: string; // Pour forcer une marque spécifique (tests, développement)
}


export const BrandProvider: React.FC<BrandProviderProps> = ({ 
  children, 
  // forceBrand 
}) => {
  const brandTheme = useBrandTheme();
  
  const isFeatureEnabled = (feature: keyof BrandConfig['features']): boolean => {
    return brandTheme.features[feature];
  };
  const updatePageMeta = useCallback(() => {
    if (document.title !== brandTheme.meta.title) {
      document.title = brandTheme.meta.title;
    }
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && metaDescription.getAttribute('content') !== brandTheme.meta.description) {
      metaDescription.setAttribute('content', brandTheme.meta.description);
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords && metaKeywords.getAttribute('content') !== brandTheme.meta.keywords.join(', ')) {
      metaKeywords.setAttribute('content', brandTheme.meta.keywords.join(', '));
    }
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon && favicon.href !== brandTheme.assets.favicon) {
      favicon.href = brandTheme.assets.favicon;
    }
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor && themeColor.getAttribute('content') !== brandTheme.colors.primary) {
      themeColor.setAttribute('content', brandTheme.colors.primary);
    }
  }, [brandTheme]);

  useEffect(() => {
    updatePageMeta();
  }, [brandTheme.name, updatePageMeta]);

  // Police, couleur de texte et couleurs de boutons de la marque exposées en
  // variables CSS, pour les éléments rendus hors de React (popups SweetAlert
  // notamment), qui ne peuvent pas recevoir de classes Tailwind.
  useEffect(() => {
    const root = document.documentElement;
    const { primary, error, text } = brandTheme.colors;
    root.style.setProperty('--brand-font-family', `'${brandTheme.fontFamily}'`);
    root.style.setProperty('--brand-text-color', text);
    root.style.setProperty('--brand-primary-color', primary);
    root.style.setProperty('--brand-primary-rgb', hexToRgbTriplet(primary));
    root.style.setProperty('--brand-error-color', error);
    root.style.setProperty('--brand-error-rgb', hexToRgbTriplet(error));
    root.style.setProperty('--brand-neutral-rgb', hexToRgbTriplet(text));
  }, [brandTheme.fontFamily, brandTheme.colors]);
  
  useEffect(() => {
    updatePageMeta();
  }, [updatePageMeta]);
  
  const contextValue: BrandContextType = {
    brandConfig: brandTheme,
    brandTheme,
    isFeatureEnabled,
    updatePageMeta
  };
  
  return (
    <BrandContext.Provider value={contextValue}>
      {children}
    </BrandContext.Provider>
  );
};


export const useBrandContext = (): BrandContextType => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrandContext must be used within a BrandProvider');
  }
  return context;
};


export const useBrand = (): BrandConfig => {
  const { brandConfig } = useBrandContext();
  return brandConfig;
};


export const useBrandThemeContext = (): BrandTheme => {
  const { brandTheme } = useBrandContext();
  return brandTheme;
};

/**
 * Hook pour vérifier si une fonctionnalité est activée
 */
export const useFeatureFlag = (feature: keyof BrandConfig['features']): boolean => {
  const { isFeatureEnabled } = useBrandContext();
  return isFeatureEnabled(feature);
};

export default BrandProvider;
